import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import moment from "moment";
import { GridColDef } from "@mui/x-data-grid";
import { showError } from "../../constants/toasts";
import { Freelancer, Tasks } from "../../types/General";
import {
  useLazyGetFreelancerAssignedQuery,
  useLazyGetTaskDetailByIdQuery,
} from "../../services/users";
import useTranslation from "../../hooks/traslation";
import { useNavigate, useParams } from "react-router-dom";
import useSkills from "../../hooks/useSkills";
import { formatSkillsDisplay } from "../../utils/skillsIndustriesHelpers";
import type { PageChangeHandler } from "../../types/api";
import { getErrorMessage } from "../../types/api";
import { Box } from "@mui/material";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import TableComponent from "../../components/TableComponent";
import RejectReasonModal from "../../Modals/rejectReason";

function TaskDetails() {
  const navigate = useNavigate();
  const { id, employerId } = useParams();
  const translation = useTranslation();
  const { skills } = useSkills({ fetchOnMount: true });
  const [getTaskDetailMethod] = useLazyGetTaskDetailByIdQuery();
  const [freelancerListMethod] = useLazyGetFreelancerAssignedQuery();
  const [searchTerm, setSearchTerm] = useState("");

  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskDetail, setTaskDetail] = useState<Tasks | null>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [rows, setRows] = useState<Freelancer[]>([]);
  const [count, setCount] = useState(0);
  const handleCloseModal = () => {
    setOpen(false);
  };

  const handlePageChange: PageChangeHandler = (event, newPage) => {
    setPage(newPage + 1);
  };

  const getTaskDetail = async (id: string) => {
    setLoading(true);
    try {
      const response = await getTaskDetailMethod({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setTaskDetail(response?.data || null);
      }
    } catch (error: unknown) {
      showError(getErrorMessage(error));
      setLoading(false);
    }
  };

  const getFreelancerList = async (id: string) => {
    setLoading(true);
    try {
      const response = await freelancerListMethod({
        id,
        page,
        limit: 10,
        search: debouncedSearchTerm,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setRows(
          (response?.data?.list &&
            response?.data?.list.map((item: Freelancer, index: number) => ({
              ...item,
              _id: item?._id + index,
            }))) ||
            []
        );
        setCount(response?.data?.totalCount || null);
      }
    } catch (error: unknown) {
      showError(getErrorMessage(error));
      setLoading(false);
    }
  };

  const formatTaskDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) return "-";

    const duration = moment.duration(moment(endTime).diff(moment(startTime)));
    const hours = duration.hours();
    const minutes = duration.minutes();

    return `${hours > 0 ? `${hours} h ` : ""}${minutes} min`;
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 220,
      field: "maintenance",
      headerName: "Freelancer Detail",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(
                `/manage-freelancer/detail/${row?.freelancerDetails?._id}`
              )
            }
          >
            <figure>
              <img
                src={
                  row?.freelancerDetails?.image ||
                  "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>
                {row?.freelancerDetails?.firstName || "-"}{" "}
                {row?.freelancerDetails?.lastName || ""}
              </b>{" "}
              <span>{row?.freelancerDetails?.email || "-"}</span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "est",
      headerName: "Price",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong>AED {row?.subTotal || "0"}</strong>
          </p>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "offered",
      headerName: "Task Duration",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p>
            <strong>
              {formatTaskDuration(row?.startTime || "", row?.endTime || "")}
            </strong>
          </p>
        );
      },
    },

    {
      minWidth: 150,
      field: "date",
      headerName: translation.main.createdAt,
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return <p>{moment(row?.createdAt).format("YYYY-MM-DD")}</p>;
      },
    },
  ];

  useEffect(() => {
    if (id) {
      getFreelancerList(id);
    }
  }, [debouncedSearchTerm, id]);

  useEffect(() => {
    if (id) {
      getTaskDetail(id);
    }
  }, [id]);

  return (
    <>
      <MainContainer>
        <div className="main_title">
          <h1>Task Details</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>
              {translation.dashboard.dashboard}
            </span>{" "}
            -{" "}
            <span onClick={() => navigate(`/manage-tasks`)}>Manage Tasks</span>
          </p>
        </div>

        <div className="cards view_page">
          <div className="section_title">
            {/* <h2>{translation.main.taskDetail}</h2> */}
          </div>
          <div className="gap_p">
            <div className="w_33">
              <h6>{translation.globals.name}</h6>
              <p>
                {" "}
                {taskDetail?.category?.name || ""},{" "}
                {taskDetail?.subCategory?.name || ""}
              </p>
            </div>
            <div className="w_33">
              <h6>{translation.main.Category}</h6>
              <p>{taskDetail?.category?.name || ""}</p>
            </div>
            <div className="w_33">
              <h6>{translation.main.subService}</h6>
              <p>{taskDetail?.subCategory?.name || ""}</p>
            </div>

            <div className="w_33">
              <h6>Sub Total</h6>
              <p>AED {taskDetail?.subTotal || "0"}</p>
            </div>
            <div className="w_33">
              <h6>Total</h6>
              <p>AED {taskDetail?.total || "0"}</p>
            </div>
            <div className="w_33">
              <h6>Start Time</h6>
              <p>
                {taskDetail?.startTime
                  ? moment(taskDetail?.startTime).format("lll")
                  : ""}
              </p>
            </div>
            <div className="w_33">
              <h6>End Time</h6>
              <p>
                {taskDetail?.endTime
                  ? moment(taskDetail?.endTime).format("lll")
                  : ""}
              </p>
            </div>
            <div className="w_33">
              <h6>Skills Required</h6>
              <p>
                {formatSkillsDisplay(skills, taskDetail?.skills)}
              </p>
            </div>
            {/* <div className="w_33">
              <h6>Education Required</h6>
              <p>Graduation</p>
            </div> */}
            <div className="w_100">
              <h6>Job Description</h6>
              <p>{taskDetail?.notes || "-"}</p>
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="section_title">
            <h2> Freelancer Assigned </h2>
          </div>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  placeholder={translation.user.SearchHere}
                  searchTerm={searchTerm}
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: React.ChangeEvent<HTMLInputElement>) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
          </div>
          <Box className="custom_tabs" sx={{ width: "100%" }}>
            <div className="sorting_table">
              {
                <TableComponent
                  columns={columns}
                  rows={rows}
                  count={count}
                  page={page}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              }
            </div>
          </Box>
        </div>
      </MainContainer>

      <RejectReasonModal
        open={open}
        onClose={handleCloseModal}
        setOpen={setOpen}
      />
    </>
  );
}

export default TaskDetails;
